import { UserInfo } from '@amzn/awscat-portal-authentication-library';
import { AssumptionService } from '@amzn/awscat-portfolio-assessment-service-client';
import { AssessmentConfiguration, AssumptionMeta, LoadAssumptionListConfig, UpdateAssumptionConfig } from '@amzn/awscat-portfolio-assessment-types';
import { AuthContextInterface, FlashContextInterface, FlashType, withAuthContext, withFlashContext } from '@amzn/awscat-react-components';
import { Box, Button, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { useEffect, useState } from 'react';
import { useIsMounted } from 'react-tidy';

import { toDetailedDate } from '../../common/DateDisplay';
import { constants } from '../../common/Localization';
import AWSCATTable from '../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes, Query } from '../../common/table/types';
import { deepCopy } from '../../common/util';
import { getFilterQuery } from '../portfolio-data-asset/utils';
import { ASSUMPTION_PREFIX, ASSUMPTION_TYPE } from './tco-assumptions-modal-helper';

type Props = AuthContextInterface & FlashContextInterface & {
    configuration: AssessmentConfiguration;
    cancel: () => void;
    onConfirmLoadAssumption: (assumption: AssessmentConfiguration, assumptionName: string) => void;
    onSaveAssumption: (assumptionName: string) => void;
    isSaveAsExisting: boolean;
    assumptionName?: string;
    title: string;
};
const assumptionService = new AssumptionService();

enum ASSUMPTIONS_TABLE_COLUMNS {
    NAME = 'name',
    UPDATED_AT = 'updatedAt',
    OWNER = 'owner',
}

function TcoAssumptionList(props: Props): JSX.Element {
    const [ loading, setLoading ] = useState(true);
    const [ userInfo, setUserInfo ] = useState<UserInfo>();
    const [ assumptionList, setAssumptionList ] = useState<AssumptionMeta[]>([]);
    const [ selectedAssumption, setSelectedAssumption ] = useState<AssumptionMeta | null>();
    const [ defaultQuery ] = useState<Query | undefined>(props?.assumptionName ?
        getFilterQuery([props?.assumptionName], ASSUMPTIONS_TABLE_COLUMNS.NAME) : undefined);
    const [ saveLoading, setSaveLoading ] = useState(false);
    const [ deleteLoading, setDeleteLoading ] = useState(false);

    const isMounted = useIsMounted();

    useEffect(() => {
        const loadAssumptions = async () => {
            setLoading(true);
            try {
                const userInformation = await props.auth.getUserInfo();
                if (isMounted()) {
                    setUserInfo(userInformation);
                }
                
                const loadAssumptionListConfig: LoadAssumptionListConfig = {
                    config: {
                        prefix: ASSUMPTION_PREFIX,
                        resourceId: userInformation.userId,
                        type: ASSUMPTION_TYPE,
                    },
                    data: null
                };
                const result = await assumptionService.loadList(loadAssumptionListConfig);
                if (isMounted()) {
                    setAssumptionList(result.metadata);
                    setLoading(false);
                } 
            } catch (err) {
                props.flash.alert(FlashType.error, (err as Error).message, constants.LOAD_ASSUMPTION_ERROR);
            }
        };

        loadAssumptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatPrincipalName = (owner: string): string => {
        if (userInfo && userInfo.userId === owner) {
            return userInfo.displayName;
        }

        const lastIndexPos = owner.lastIndexOf('_');
        return owner.substring(lastIndexPos + 1);
    };

    const tcoAssumptionListColumnDefs: ColumnDefinition<AssumptionMeta>[] = [
        {
            id: ASSUMPTIONS_TABLE_COLUMNS.NAME,
            header: constants.NAME,
            cell: item => item.name,
            type: ColumnTypes.TEXT,
        }, {
            id: ASSUMPTIONS_TABLE_COLUMNS.UPDATED_AT,
            header: constants.DATE_MODIFIED,
            data: item => toDetailedDate(new Date(item.updatedAt)),
            cell: item => toDetailedDate(new Date(item.updatedAt)),
            type: ColumnTypes.TEXT,
        }, {
            id: ASSUMPTIONS_TABLE_COLUMNS.OWNER,
            header: constants.OWNER,
            cell: item => formatPrincipalName(item.owner),
            type: ColumnTypes.TEXT,
        }
    ];

    const onAssumptionSelectionChange = (assumptions: readonly AssumptionMeta[] | undefined) => {
        const selected = assumptions?.length ? assumptions[0] : null;
        setSelectedAssumption(selected);
    };

    const onClickPrimaryBtn = async () => {
        setSaveLoading(true);
        if (props.isSaveAsExisting) {
            await onConfirmSave();
        } else {
            await onConfirmLoad();
        }
    };

    const onConfirmLoad = async () => {
        if (!selectedAssumption) {
            return;
        }
        const newAssumption = await assumptionService.loadById(selectedAssumption.id);
        props.onConfirmLoadAssumption(newAssumption, selectedAssumption.name);
    };

    const onConfirmSave = async () => {
        if (!userInfo || !userInfo.userId) {
            props.flash.alert(FlashType.error, constants.INVALID_USER_INFO, '');
            return;
        }
        if (!selectedAssumption || !props.configuration) {
            return;
        }
        const updateConfig: UpdateAssumptionConfig = {
            config: {
                resourceId: userInfo.userId,
                type: ASSUMPTION_TYPE,
            },
            data: props.configuration
        };

        await assumptionService.save(selectedAssumption.id, updateConfig);
        props.onSaveAssumption(selectedAssumption.name);
    };

    const onConfirmDelete = async () => {
        if (!selectedAssumption) {
            return;
        }
        setDeleteLoading(true);
        await assumptionService.deleteById(selectedAssumption.id);
        const deepCopiedAssumptionList: AssumptionMeta[] = deepCopy(assumptionList);
        const index = deepCopiedAssumptionList.findIndex((assumption: AssumptionMeta) => assumption.id === selectedAssumption.id);
        if (index > -1) {
            deepCopiedAssumptionList.splice(index, 1);
        }

        setAssumptionList(deepCopiedAssumptionList);
        setDeleteLoading(false);
    };

    return loading ? (
        <Spinner size='large' />
    ) : (
        <SpaceBetween direction='vertical' size='m'>
            <Box variant='h2'>{props.title}</Box>
            <Box>
                <AWSCATTable
                    enableAutoSorting={true}
                    resourceName='Assumptions'
                    items={assumptionList}
                    columnDefinitions={tcoAssumptionListColumnDefs}
                    selectionType='single'
                    onSelectionChange={onAssumptionSelectionChange}
                    defaultQuery={defaultQuery}
                />

                <Box float='left' margin={{top: 'm'}}>
                    <Button
                        disabled={!selectedAssumption || saveLoading}
                        loading={deleteLoading}
                        onClick={onConfirmDelete}
                    >{constants.DELETE_BUTTON_TEXT}
                    </Button>
                </Box>
                <Box float='right' margin={{top: 'm'}}>
                    <SpaceBetween direction='horizontal' size='s'>
                        <Button
                            onClick={props.cancel}
                            disabled={saveLoading || deleteLoading}
                        >{constants.CANCEL}
                        </Button>
                        <Button
                            loading={saveLoading}
                            disabled={!selectedAssumption || deleteLoading}
                            variant='primary'
                            onClick={onClickPrimaryBtn}
                            id='btn-load-tco-assumption'
                        >{props.isSaveAsExisting ? constants.SAVE_AND_APPLY : constants.LOAD_AND_APPLY}
                        </Button>
                    </SpaceBetween>
                </Box>
            </Box>
        </SpaceBetween>
    );
};

export default withAuthContext(withFlashContext(TcoAssumptionList));
